import React, { useState, useEffect } from "react";
import { Table, Tag, Upload, Button, message, Modal, Form, Select, Input, Checkbox, Spin } from "antd";
import Dragger from "antd/es/upload/Dragger";
import { UploadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { UploadProps } from "antd/es/upload";
import axios from "axios";
import { sub } from "date-fns";
import { getEndpoint } from '../utils/endpoints';

interface MemoryUpload {
  key: string;
  date: Date;
  client: string;
  subclient: string;
  domain: string[];
  category: string[];
  source: string;
  target: string;
  rows: number;
  status: "uploaded" | "processed" | "cancelled";
}

const UploadMemories: React.FC = () => {
  const [queue, setQueue] = useState<MemoryUpload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState<any>(null);
  const [confirmFile, setConfirmFile] = useState<File | null>(null);
  const [replace, setReplace] = useState(false);
  const [clients, setClients] = useState<string[]>([]);
  const [form] = Form.useForm();

  // Fetch memory file summaries from backend
  const fetchMemories = async () => {
    setUploading(true);
    try {
      const response = await axios.get(`${getEndpoint('BACKEND')}/getMemoriesSummary/`);
      // Map backend summary to MemoryUpload type (now from memories collection)
      const data = (response.data || []).map((item: any, idx: number) => ({
        key: item.memory_code || idx,
        date: item.upload_date || item.date,
        client: item.client,
        subclient: item.subclient,
        domain: item.domain,
        category: item.category,
        source: item.source_lang,
        target: item.target_lang,
        rows: item.rows,
        status: item.status,
      }));
      setQueue(data);
    } catch (err) {
      message.error('Errore nel caricamento delle memorie.');
    }
    setUploading(false);
  };

  useEffect(() => {
    fetchMemories();
  }, []);

  // Fetch clients from backend
  const fetchClients = async () => {
    try {
      const response = await axios.get(`${getEndpoint('BACKEND')}/getClients/`);
      // L'API restituisce un array di oggetti, prendi il campo client_code o client_name
      const data = response.data || [];
      // Supporta sia client_code che client_name
      const clientList = data.map((c: any) => c.client_code || c.client_name || c);
      setClients(clientList);
    } catch (err) {
      setClients([]);
    }
  };

  // Quando la modale si apre, carica i clienti
  useEffect(() => {
    if (confirmModal) {
      fetchClients();
    }
    // eslint-disable-next-line
  }, [confirmModal]);

  const beforeUpload: UploadProps["beforeUpload"] = async (file) => {
    setValidating(true);
    // Validate filename: <client>-<source>-<target>.<ext>
    // Validate content (client-side, only for CSV)
    if (file.name.endsWith(".csv")) {
      const text = await file.text();
      const firstLine = text.split("\n")[0].toLowerCase();
      if (!firstLine.includes("source") || !firstLine.includes("target")) {
        message.error("CSV must have 'source' and 'target' columns.");
        setValidating(false);
        return Upload.LIST_IGNORE;
      }
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(
        `${getEndpoint('BACKEND')}/validateMemory/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.status === 200) {
        setConfirmData(response.data);
        setConfirmFile(file);
        setConfirmModal(true);
        form.setFieldsValue({
          domain: response.data.domain,
          category: response.data.category,
          description: "",
        });
      } else {
        message.error(response.data.error || "Validation failed");
      }
    } catch (err: any) {
      message.error(err?.response?.data?.error || "Validation failed");
    }
    setUploading(false);
    setValidating(false);
    return false; // Prevent auto upload
  };

  const handleConfirm = async () => {
    if (!confirmFile) return;
    setUploading(true);
    const values = await form.validateFields();
    const formData = new FormData();
    formData.append("file", confirmFile);
    formData.append("domain", values.domain);
    formData.append("category", values.category);
    formData.append("description", values.description || "");
    formData.append("replace", replace ? "true" : "false");
    formData.append("client", confirmData.client || "");
    formData.append("subclient", values.subclient || "");
    formData.append("source", confirmData.source || "");
    formData.append("target", confirmData.target || "");
    try {
      const response = await axios.post(
        `${getEndpoint('BACKEND')}/uploadMemory/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.status === 200) {
        message.success("File uploaded!");
        fetchMemories();
      } else {
        message.error(response.data.error || "Upload failed");
      }
    } catch (err: any) {
      message.error(err?.response?.data?.error || "Upload failed");
    }
    setUploading(false);
    setConfirmModal(false);
    setConfirmData(null);
    setConfirmFile(null);
    setReplace(false);
  };

  const columns: ColumnsType<MemoryUpload> = [
    {
      title: "Last Upload Date",
      dataIndex: "date",
      key: "date",
      render: (date: Date) => (date ? new Date(date).toLocaleString() : ""),
      sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      defaultSortOrder: "descend",
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      sorter: (a, b) => (a.client || "").localeCompare(b.client || ""),
      filters: Array.from(new Set(queue.map((item) => item.client))).map((client) => ({ text: client, value: client })),
      onFilter: (value, record) => record.client === value,
    },
    {
      title: "Subclient",
      dataIndex: "subclient",
      key: "subclient",
      sorter: (a, b) => (a.subclient || "").localeCompare(b.subclient || ""),
      filters: Array.from(new Set(queue.map((item) => item.subclient))).map((subclient) => ({ text: subclient, value: subclient })),
      onFilter: (value, record) => record.client === value,
    },

    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render: (s) => (s ? s.toUpperCase() : ""),
      sorter: (a, b) => (a.source || "").localeCompare(b.source || ""),
      filters: Array.from(new Set(queue.map((item) => item.source))).map((source) => ({
        text: (source || "").toUpperCase(),
        value: source,
      })),
      onFilter: (value, record) => record.source === value,
    },
    {
      title: "Target",
      dataIndex: "target",
      key: "target",
      render: (t) => (t ? t.toUpperCase() : ""),
      sorter: (a, b) => (a.target || "").localeCompare(b.target || ""),
      filters: Array.from(new Set(queue.map((item) => item.target))).map((target) => ({
        text: (target || "").toUpperCase(),
        value: target,
      })),
      onFilter: (value, record) => record.target === value,
    },
    {
      title: "Domains",
      dataIndex: "domain",
      key: "domain",
      render: (domains) => (Array.isArray(domains) ? domains : [domains]).filter(Boolean).map((d) => <Tag key={d}>{d}</Tag>),
      filters: Array.from(new Set(queue.flatMap((item: any) => item.domain || []))).map((domain) => ({ text: domain, value: domain })),
      onFilter: (value, record: any) => (record.domain || []).includes(value),
    },
    {
      title: "Categories",
      dataIndex: "category",
      key: "category",
      render: (categories) => (Array.isArray(categories) ? categories : [categories]).filter(Boolean).map((c) => <Tag key={c}>{c}</Tag>),
      filters: Array.from(new Set(queue.flatMap((item: any) => item.category || []))).map((cat) => ({ text: cat, value: cat })),
      onFilter: (value, record: any) => (record.category || []).includes(value),
    },
    {
      title: "Rows",
      dataIndex: "rows",
      key: "rows",
      sorter: (a, b) => (a.rows || 0) - (b.rows || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color = "blue";
        if (status === "uploaded") color = "green";
        if (status === "cancelled") color = "red";
        if (status === "processed") color = "gold";
        return <Tag color={color}>{(status || "").toUpperCase()}</Tag>;
      },
      filters: [
        { text: "Uploaded", value: "uploaded" },
        { text: "Processed", value: "processed" },
        { text: "Cancelled", value: "cancelled" },
      ],
      onFilter: (value, record) => record.status === value,
    },
  ];

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: 32 }}>
      <h2 style={{ marginBottom: 24 }}>Upload Memories</h2>
      {(uploading || validating) && <div style={{textAlign:'center',marginBottom:16}}><Spin tip="Loading..." size="large" /></div>}
      <Dragger
        beforeUpload={beforeUpload}
        showUploadList={false}
        accept=".csv,.xlsx,.xls"
        disabled={uploading || validating}
        style={{ marginBottom: 24 }}
      >
        {validating ? <Spin tip="Validating file..." /> : (
          <>
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">Click or drag file to this area to upload</p>
            <p className="ant-upload-hint">
              Filename must be in .csv|xlsx|xls format. File must have 'source' and 'target' columns.
            </p>
          </>
        )}
      </Dragger>
      <Modal
        open={confirmModal}
        title="Confirm Memory Upload"
        onCancel={() => {
          setConfirmModal(false);
          setConfirmData(null);
          setConfirmFile(null);
          setReplace(false);
        }}
        onOk={handleConfirm}
        okText="Upload"
        confirmLoading={uploading}
        destroyOnClose
      >
        {confirmData && (
          <Form
            form={form}
            initialValues={{
              domain: confirmData.domain,
              category: confirmData.category,
              subclient: confirmData.subclient,
              description: ""
            }}
          >
            <Form.Item label={<b>Owner</b>} style={{ display: 'none' }}>
              {confirmData.client}
            </Form.Item>
            <Form.Item name="subclient" label={<b>Cliente</b>} rules={[{ required: true }]}>
              <Select
                placeholder="Seleziona cliente"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children).toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
                defaultValue={confirmData.subclient}
              >
                {clients.map((client) => (
                  <Select.Option key={client} value={client}>{client}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label={<b>Date</b>} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{confirmData.date}</span>
            </Form.Item>
            <Form.Item label={<b>Source</b>} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{confirmData.source}</span>
            </Form.Item>
            <Form.Item label={<b>Target</b>} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{confirmData.target}</span>
            </Form.Item>
            <Form.Item label={<b>Rows</b>} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{confirmData.rows}</span>
            </Form.Item>
            <Form.Item name="domain" label="Domain" rules={[{ required: true }]} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Select defaultValue={confirmData.domain} style={{ width: '100%' }}>
                <Select.Option value="patents">patents</Select.Option>
                <Select.Option value="legal">legal</Select.Option>
                <Select.Option value="medical">medical</Select.Option>
                <Select.Option value="travel">medical</Select.Option>
                <Select.Option value="cooking">medical</Select.Option>
                <Select.Option value="other">medical</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="category" label="Category" rules={[{ required: true }]} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Select  defaultValue={confirmData.category} style={{ width: '100%' }}>
                <Select.Option value="generic">generic</Select.Option>
                <Select.Option value="biotech">biotech</Select.Option>
                <Select.Option value="chemistry">chemistry</Select.Option>
                <Select.Option value="electronics">electronics</Select.Option>
                <Select.Option value="mechanics">mechanics</Select.Option>
                <Select.Option value="TLC">TLC</Select.Option>
                <Select.Option value="medical">medical</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea placeholder="Add a description for this memory upload (optional)" />
            </Form.Item>
            <Form.Item>
              <Checkbox checked={replace} onChange={e => setReplace(e.target.checked)}>
                Replace all memories for this client and direction (full replace)
              </Checkbox>
            </Form.Item>
          </Form>
        )}
      </Modal>
      <Table
        columns={columns}
        dataSource={queue}
        rowKey="key"
        pagination={false}
        style={{ marginTop: 32 }}
      />
    </div>
  );
};

export default UploadMemories;
